import React, { useCallback } from 'react';

import { Clear as ClearIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

import {
  CUSTOM_RULE_TYPE_OPTIONS,
  type CustomRuleType,
  MAX_END_DATE,
  MIN_START_DATE,
  POINTS_TYPE_OPTIONS,
  PRODUCT_TYPE_OPTIONS,
  type PointsType,
  type ProductType,
} from '~/types/loyaltyContract';

interface SearchFiltersSectionProps {
  themeColor: string;
  // Filter values
  customRuleIdInput: string;
  nameInput: string;
  offerIdInput: string;
  packageIdInput: string;
  regionInput: string;
  earnKeyInput: string;
  promoKeyInput: string;
  selectedProductType: ProductType | '';
  selectedType: CustomRuleType | '';
  selectedPointsType: PointsType | '';
  startDateFrom: string;
  endDateTo: string;

  setCustomRuleIdInput: (value: string) => void;
  setNameInput: (value: string) => void;
  setOfferIdInput: (value: string) => void;
  setPackageIdInput: (value: string) => void;
  setRegionInput: (value: string) => void;
  setEarnKeyInput: (value: string) => void;
  setPromoKeyInput: (value: string) => void;
  setSelectedProductType: (value: ProductType | '') => void;
  setSelectedType: (value: CustomRuleType | '') => void;
  setSelectedPointsType: (value: PointsType | '') => void;
  setStartDateFrom: (value: string) => void;
  setEndDateTo: (value: string) => void;

  // Actions
  onSearch: () => void;
  onClear: () => void;

  // Validation errors
  validationErrors?: Record<string, string>;
}

export default function SearchFiltersSection({
  themeColor,
  customRuleIdInput,
  nameInput,
  offerIdInput,
  packageIdInput,
  regionInput,
  earnKeyInput,
  promoKeyInput,
  selectedProductType,
  selectedType,
  selectedPointsType,
  startDateFrom,
  endDateTo,
  setCustomRuleIdInput,
  setNameInput,
  setOfferIdInput,
  setPackageIdInput,
  setRegionInput,
  setEarnKeyInput,
  setPromoKeyInput,
  setSelectedProductType,
  setSelectedType,
  setSelectedPointsType,
  setStartDateFrom,
  setEndDateTo,
  onSearch,
  onClear,
  validationErrors = {},
}: SearchFiltersSectionProps) {
  const theme = useTheme();

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onSearch();
    },
    [onSearch],
  );

  // Shared input styles
  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
    },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        bgcolor: alpha(theme.palette.background.paper, 0.6),
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={2}>
          {/* Row 1: ID + Name */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Custom Rule ID"
              value={customRuleIdInput}
              onChange={(e) => setCustomRuleIdInput(e.target.value)}
              error={!!validationErrors.ids}
              helperText={validationErrors.ids}
              fullWidth
              size="small"
              sx={inputStyles}
            />
            <TextField
              label="Name Search"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              error={!!validationErrors.name}
              helperText={validationErrors.name}
              fullWidth
              size="small"
              sx={inputStyles}
            />
          </Stack>

          {/* Row 2: Keys */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Earn Key"
              value={earnKeyInput}
              onChange={(e) => setEarnKeyInput(e.target.value)}
              error={!!validationErrors.earnKeys}
              helperText={validationErrors.earnKeys}
              fullWidth
              size="small"
              sx={inputStyles}
            />
            <TextField
              label="Promo Key"
              value={promoKeyInput}
              onChange={(e) => setPromoKeyInput(e.target.value)}
              error={!!validationErrors.promoKeys}
              helperText={validationErrors.promoKeys}
              fullWidth
              size="small"
              sx={inputStyles}
            />
          </Stack>

          {/* Row 3: Offer + Package */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Offer ID"
              value={offerIdInput}
              onChange={(e) => setOfferIdInput(e.target.value)}
              error={!!validationErrors.offerIds}
              helperText={validationErrors.offerIds}
              fullWidth
              size="small"
              sx={inputStyles}
            />
            <TextField
              label="Package ID"
              value={packageIdInput}
              onChange={(e) => setPackageIdInput(e.target.value)}
              error={!!validationErrors.packageIds}
              helperText={validationErrors.packageIds}
              fullWidth
              size="small"
              sx={inputStyles}
            />
          </Stack>

          {/* Row 4: Types */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth size="small" error={!!validationErrors.productTypes}>
              <InputLabel>Product Type</InputLabel>
              <Select
                value={selectedProductType}
                label="Product Type"
                onChange={(e: SelectChangeEvent<ProductType | ''>) => setSelectedProductType(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">
                  <Typography sx={{ fontStyle: 'italic', color: 'text.secondary' }}>All</Typography>
                </MenuItem>
                {PRODUCT_TYPE_OPTIONS.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" error={!!validationErrors.types}>
              <InputLabel>Type</InputLabel>
              <Select
                value={selectedType}
                label="Type"
                onChange={(e: SelectChangeEvent<CustomRuleType | ''>) => setSelectedType(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">
                  <Typography sx={{ fontStyle: 'italic', color: 'text.secondary' }}>All</Typography>
                </MenuItem>
                {CUSTOM_RULE_TYPE_OPTIONS.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" error={!!validationErrors.pointsTypes}>
              <InputLabel>Points Type</InputLabel>
              <Select
                value={selectedPointsType}
                label="Points Type"
                onChange={(e: SelectChangeEvent<PointsType | ''>) => setSelectedPointsType(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">
                  <Typography sx={{ fontStyle: 'italic', color: 'text.secondary' }}>All</Typography>
                </MenuItem>
                {POINTS_TYPE_OPTIONS.filter((type) => type === 'BONUS_POINTS' || type === 'BONUS_STATUS_CREDITS').map(
                  (type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          </Stack>

          {/* Row 5: Dates + Actions */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-end">
            <TextField
              label="Start Date From"
              type="date"
              value={startDateFrom}
              onChange={(e) => setStartDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: MIN_START_DATE,
                max: MAX_END_DATE,
              }}
              error={!!validationErrors.startDateFrom}
              helperText={validationErrors.startDateFrom}
              fullWidth
              size="small"
              sx={inputStyles}
            />
            <TextField
              label="End Date To"
              type="date"
              value={endDateTo}
              onChange={(e) => setEndDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: startDateFrom || MIN_START_DATE,
                max: MAX_END_DATE,
              }}
              error={!!validationErrors.endDateTo}
              helperText={validationErrors.endDateTo}
              fullWidth
              size="small"
              sx={inputStyles}
            />

            <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<SearchIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  minWidth: 110,
                  height: 40,
                  backgroundColor: themeColor,
                }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={onClear}
                sx={{
                  color: themeColor,
                  borderRadius: 2,
                  textTransform: 'none',
                  minWidth: 90,
                  height: 40,
                  border: `1px solid ${themeColor}`,
                }}
              >
                Clear
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
}
